import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Paperclip, User, Mail, X, FileText, Image as ImageIcon, File } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { showToast } from '../utils/toast';
import { formatRelativeTimeEST, formatDateEST } from '../utils/timezone';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ClientMessaging() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [pmInfo, setPmInfo] = useState({ name: '', email: '' });
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Only auto-scroll on initial load or when user sends a message
    if (isInitialLoad.current && messages.length > 0) {
      scrollToBottom();
      isInitialLoad.current = false;
    }
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${API}/api/client/messages`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        setPmInfo({
          name: data.assigned_pm || 'Project Manager',
          email: 'support@birdenmedia.co'
        });
        
        // Mark messages as read
        if (data.unread_count > 0) {
          await fetch(`${API}/api/client/messages/mark-read`, {
            method: 'PATCH',
            credentials: 'include'
          });
        }
      } else if (response.status === 401) {
        navigate('/client/login');
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast({
        title: 'File too large',
        message: 'Maximum file size is 10MB',
        type: 'error'
      });
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      showToast({
        title: 'Invalid file type',
        message: 'Only PDF, JPG, PNG, and DOCX files are allowed',
        type: 'error'
      });
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/api/client/messages/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setAttachment({
          url: data.attachment_url,
          filename: data.attachment_filename,
          type: data.attachment_type
        });
        showToast({
          title: 'File uploaded',
          message: 'File attached to message',
          type: 'success'
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast({
        title: 'Upload failed',
        message: 'Could not upload file',
        type: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageText.trim() && !attachment) {
      return;
    }

    setSending(true);

    try {
      const response = await fetch(`${API}/api/client/messages/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message_text: messageText.trim(),
          attachment_url: attachment?.url,
          attachment_filename: attachment?.filename,
          attachment_type: attachment?.type
        })
      });

      if (response.ok) {
        setMessageText('');
        setAttachment(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        await fetchMessages();
        // Scroll to bottom after sending a message
        setTimeout(() => scrollToBottom(), 100);
        showToast({
          title: 'Message sent',
          message: 'Your message has been delivered',
          type: 'success'
        });
      } else {
        throw new Error('Send failed');
      }
    } catch (error) {
      console.error('Send error:', error);
      showToast({
        title: 'Send failed',
        message: 'Could not send message',
        type: 'error'
      });
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    return formatRelativeTimeEST(timestamp);
  };

  const getAttachmentIcon = (type) => {
    switch (type) {
      case 'image': return <ImageIcon className="h-5 w-5" />;
      case 'pdf': return <FileText className="h-5 w-5" />;
      default: return <File className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden flex flex-col">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-yellow-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
      </div>

      {/* Header */}
      <header className="relative border-b border-white/10 bg-black/20 backdrop-blur-lg flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/client-dashboard')}
              className="text-white hover:bg-white/10 touch-manipulation"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-500 flex items-center justify-center shadow-lg">
                <User className="h-5 w-5 text-slate-900" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-white font-semibold text-base sm:text-lg truncate">
                  {pmInfo.name}
                </h1>
                <a 
                  href={`mailto:${pmInfo.email}`}
                  className="text-blue-300 text-xs sm:text-sm hover:text-blue-200 flex items-center gap-1 truncate"
                >
                  <Mail className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{pmInfo.email}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-white/10 mx-auto mb-4 flex items-center justify-center">
                <Mail className="h-8 w-8 text-blue-300" />
              </div>
              <p className="text-white text-lg font-semibold mb-2">No messages yet</p>
              <p className="text-blue-200 text-sm">Send a message to start the conversation</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.message_id}
                className={`flex ${msg.sender_type === 'client' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] sm:max-w-[70%] ${
                    msg.sender_type === 'client'
                      ? 'bg-gradient-to-r from-yellow-400 to-yellow-500'
                      : 'bg-white/10 backdrop-blur-xl border border-white/20'
                  } rounded-2xl p-4 shadow-lg`}
                >
                  {msg.sender_type !== 'client' && (
                    <p className="text-xs font-semibold text-blue-300 mb-1">{msg.sender_name}</p>
                  )}
                  
                  <p className={`text-sm sm:text-base whitespace-pre-wrap break-words ${
                    msg.sender_type === 'client' ? 'text-slate-900' : 'text-white'
                  }`}>
                    {msg.message_text}
                  </p>
                  
                  {msg.attachment_url && (
                    <div className={`mt-3 pt-3 border-t ${
                      msg.sender_type === 'client' ? 'border-slate-900/20' : 'border-white/20'
                    }`}>
                      <a
                        href={`${API}${msg.attachment_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-2 text-sm ${
                          msg.sender_type === 'client' 
                            ? 'text-slate-900 hover:text-slate-700' 
                            : 'text-blue-300 hover:text-blue-200'
                        }`}
                      >
                        {getAttachmentIcon(msg.attachment_type)}
                        <span className="truncate">{msg.attachment_filename}</span>
                      </a>
                      
                      {msg.attachment_type === 'image' && (
                        <img
                          src={`${API}${msg.attachment_url}`}
                          alt={msg.attachment_filename}
                          className="mt-2 rounded-lg max-w-full h-auto"
                        />
                      )}
                    </div>
                  )}
                  
                  <p className={`text-xs mt-2 ${
                    msg.sender_type === 'client' ? 'text-slate-900/60' : 'text-blue-300/60'
                  }`}>
                    {formatTime(msg.timestamp)}
                  </p>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input - Sticky Bottom */}
      <div className="relative border-t border-white/10 bg-black/20 backdrop-blur-lg flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          {attachment && (
            <div className="mb-3 bg-white/10 rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white text-sm flex-1 min-w-0">
                {getAttachmentIcon(attachment.type)}
                <span className="truncate">{attachment.filename}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAttachment(null)}
                className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
          
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              accept=".pdf,.jpg,.jpeg,.png,.docx"
              className="hidden"
            />
            
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || sending}
              className="text-white hover:bg-white/10 touch-manipulation flex-shrink-0"
            >
              {uploading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <Paperclip className="h-5 w-5" />
              )}
            </Button>
            
            <Textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
              placeholder="Type your message..."
              rows={1}
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-blue-200/50 focus:border-yellow-400 focus:ring-yellow-400/20 rounded-xl resize-none"
            />
            
            <Button
              type="submit"
              disabled={(!messageText.trim() && !attachment) || sending}
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-slate-900 font-semibold touch-manipulation flex-shrink-0"
            >
              {sending ? (
                <div className="w-5 h-5 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin"></div>
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </form>
          
          <p className="text-xs text-blue-300/60 mt-2 text-center">
            Press Enter to send • Shift+Enter for new line
          </p>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
}
